import { TILE_SIZE } from '@veriverse/shared';
import { animatedSheet, type AnimatedSheet } from './animated-sheets.js';
import { findObjectLayer, objectFootprint } from './model.js';
import { readProperties } from './properties.js';
import { ANIMATION_TYPE, ANIMATIONS_LAYER } from './rules.js';
import { type TiledMap, type TiledObject } from './types.js';

/** A looping decoration from the `animations` layer (ADR-0040). */
export interface AnimatedObject {
  readonly id: number;
  readonly sheet: string;
  readonly spec: AnimatedSheet;
  /** Top-left tile. */
  readonly x: number;
  readonly y: number;
  /** Row the picture is depth-sorted by: its bottom row. */
  readonly sortRow: number;
}

/**
 * Animated objects of a map, back to front. Hidden objects and unknown sheets are skipped. The
 * picture size comes from the catalog, so the object's own width and height are ignored.
 */
export function getAnimatedObjects(map: TiledMap): AnimatedObject[] {
  const found: AnimatedObject[] = [];
  for (const object of findObjectLayer(map, ANIMATIONS_LAYER)?.objects ?? []) {
    if (!object.visible) continue;
    const sheet = readProperties(object).sheet;
    const spec = typeof sheet === 'string' ? animatedSheet(sheet) : undefined;
    if (!spec || typeof sheet !== 'string') continue;
    const { x, y } = objectFootprint(object);
    found.push({ id: object.id, sheet, spec, x, y, sortRow: y + spec.height - 1 });
  }
  return found.sort((a, b) => a.sortRow - b.sortRow);
}

/** A new animated object with its top-left corner on tile `(tx, ty)`. */
export function newAnimatedObject(
  sheet: string,
  tx: number,
  ty: number,
  name = sheet.replace(/_/g, '-'),
): Omit<TiledObject, 'id'> {
  const spec = animatedSheet(sheet);
  return {
    name,
    type: ANIMATION_TYPE,
    x: tx * TILE_SIZE,
    y: ty * TILE_SIZE,
    width: (spec?.width ?? 1) * TILE_SIZE,
    height: (spec?.height ?? 1) * TILE_SIZE,
    rotation: 0,
    visible: true,
    properties: [{ name: 'sheet', type: 'string', value: sheet }],
  };
}
